// Utility functions for handling recurring schedules

export const generateRecurringInstances = (baseItem, recurringConfig, startDate, endDate) => {
  if (!recurringConfig || recurringConfig?.recurringType === 'none') {
    return [baseItem];
  }

  const instances = [];
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date(start?.getFullYear() + 1, start?.getMonth(), start?.getDate());
  
  let currentDate = new Date(start);
  let occurrenceCount = 0;
  const maxOccurrences = recurringConfig?.endAfterOccurrences ? parseInt(recurringConfig?.endAfterOccurrences) : Infinity;
  const recurringEndDate = recurringConfig?.endDate ? new Date(recurringConfig?.endDate) : end;

  while (currentDate <= recurringEndDate && currentDate <= end && occurrenceCount < maxOccurrences) {
    let shouldAddInstance = false;

    switch (recurringConfig?.recurringType) {
      case 'daily':
        shouldAddInstance = true;
        break;

      case 'weekly':
        const dayOfWeek = currentDate?.getDay();
        shouldAddInstance = recurringConfig?.weeklyDays?.includes(dayOfWeek);
        break;

      case 'monthly':
        const dayOfMonth = currentDate?.getDate();
        shouldAddInstance = dayOfMonth === parseInt(recurringConfig?.monthlyDay);
        break;

      case 'custom':
        shouldAddInstance = true;
        break;

      default:
        shouldAddInstance = false;
    }

    if (shouldAddInstance && currentDate >= start) {
      const dateStr = currentDate?.toISOString()?.split('T')?.[0];
      instances?.push({
        ...baseItem,
        id: `${baseItem?.id}-${dateStr}`,
        date: dateStr,
        isRecurring: true,
        recurringParentId: baseItem?.id,
        recurringConfig
      });
      occurrenceCount++;
    }

    // Move to next date based on recurring type
    switch (recurringConfig?.recurringType) {
      case 'daily':
        currentDate?.setDate(currentDate?.getDate() + 1);
        break;

      case 'weekly':
        currentDate?.setDate(currentDate?.getDate() + 1);
        break;

      case 'monthly':
        currentDate?.setMonth(currentDate?.getMonth() + 1);
        break;

      case 'custom':
        const interval = parseInt(recurringConfig?.customInterval) || 1;
        switch (recurringConfig?.customUnit) {
          case 'days':
            currentDate?.setDate(currentDate?.getDate() + interval);
            break;
          case 'weeks':
            currentDate?.setDate(currentDate?.getDate() + (interval * 7));
            break;
          case 'months':
            currentDate?.setMonth(currentDate?.getMonth() + interval);
            break;
          default:
            currentDate?.setDate(currentDate?.getDate() + interval);
        }
        break;

      default:
        currentDate?.setDate(currentDate?.getDate() + 1);
    }
  }

  return instances;
};

export const getRecurringDescription = (recurringConfig) => {
  if (!recurringConfig || recurringConfig?.recurringType === 'none') {
    return 'Does not repeat';
  }

  const weekDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  switch (recurringConfig?.recurringType) {
    case 'daily':
      return 'Repeats daily';

    case 'weekly':
      if (recurringConfig?.weeklyDays?.length > 0) {
        const days = recurringConfig?.weeklyDays
          ?.sort((a, b) => a - b)
          ?.map(d => weekDayNames?.[d])
          ?.join(', ');
        return `Repeats weekly on ${days}`;
      }
      return 'Repeats weekly';

    case 'monthly':
      return `Repeats monthly on day ${recurringConfig?.monthlyDay}`;

    case 'custom':
      const interval = recurringConfig?.customInterval;
      const unit = recurringConfig?.customUnit;
      return `Repeats every ${interval} ${unit}`;

    default:
      return 'Does not repeat';
  }
};

export const hasRecurringSchedule = (item) => {
  return item?.recurringConfig && item?.recurringConfig?.recurringType !== 'none';
};